import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchCurrentUser, logout as apiLogout, registerForceLogout } from '../services/api';

export const UserContext = createContext({
  currentUser: null,
  setCurrentUser: () => { },
  loadingUser: false,
  isLoggingOut: false,
  logout: async () => { },
});

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const logout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await apiLogout();
    } catch (err) {
      console.error('Error durante logout:', err);
    } finally {
      setCurrentUser(null);
      setIsLoggingOut(false);
    }
  }, [isLoggingOut]);

  useEffect(() => {
    registerForceLogout(logout);
    return () => registerForceLogout(null);
  }, [logout]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadUser = async () => {
      try {
        // api.js interceptará un 401 si el token expiró, renovará y reintentará automáticamente
        const res = await fetchCurrentUser({ signal: controller.signal });
        const user = res?.data ?? null;

        if (isMounted) {
          setCurrentUser(user);
        }
      } catch (err) {
        const isAbort = err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.message === 'canceled';
        if (!isAbort && isMounted) {
          setCurrentUser(null);
        }
      } finally {
        if (isMounted) {
          setLoadingUser(false);
        }
      }
    };

    loadUser();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  return (
    <UserContext.Provider value={{ currentUser, setCurrentUser, loadingUser, isLoggingOut, logout }}>
      {children}
    </UserContext.Provider>
  );
};